import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import api from '../../services/api';
import type { AccountBudgetProgressDto, BudgetTargetMode, TaskBudgetBreakdownRow } from '../../types/accountBudget';
import type { TaskEconomyRow } from '../../types/accountTaskEconomy';
import { formatAccountMetricValue } from '../../utils/accountMetricFormat';
import { formatTasksSectionTitle } from '../../utils/accountDashboardFocus';
import { useAccountDashboard } from './AccountDashboardContext';

interface TaskRow {
  id: string;
  name: string;
  taskNumber?: string;
  isBillable: boolean;
  isActive: boolean;
}

interface AccountTasksTableProps {
  tasks: TaskRow[];
  tasksLoading: boolean;
  canEdit: boolean;
  economyVisible: boolean;
  budgetProgress: AccountBudgetProgressDto | null;
  onOpenTask: (taskId: string, taskName: string) => void;
  onEditTask: (task: TaskRow) => void;
  onDeleteTask: (taskId: string) => void;
  onAddTask: () => void;
  onTargetsSaved?: () => void;
}

function mergeTaskRows(
  tasks: TaskRow[],
  economyRows: TaskEconomyRow[],
  breakdown: TaskBudgetBreakdownRow[]
) {
  const economyMap = new Map(economyRows.map((r) => [r.taskId, r]));
  const breakdownMap = new Map(breakdown.map((r) => [r.taskId, r]));

  return tasks.map((task) => ({
    task,
    economy: economyMap.get(task.id),
    breakdown: breakdownMap.get(task.id)
  }));
}

function formatTaskLabel(task: TaskRow): string {
  return task.taskNumber ? `${task.taskNumber} - ${task.name}` : task.name;
}

export default function AccountTasksTable({
  tasks,
  tasksLoading,
  canEdit,
  economyVisible,
  budgetProgress,
  onOpenTask,
  onEditTask,
  onDeleteTask,
  onAddTask,
  onTargetsSaved
}: AccountTasksTableProps) {
  const { accountId, metric, granularity, anchor, focusWindow, currencyCode, budgetConfig } =
    useAccountDashboard();

  const [economyRows, setEconomyRows] = useState<TaskEconomyRow[]>([]);
  const [economyLoading, setEconomyLoading] = useState(false);
  const [editingTargetTaskId, setEditingTargetTaskId] = useState<string | null>(null);
  const [targetDraft, setTargetDraft] = useState('');
  const [savingTaskId, setSavingTaskId] = useState<string | null>(null);

  const targetMode: BudgetTargetMode | null = budgetProgress?.targetMode ?? budgetConfig?.budget?.targetMode ?? null;
  const hasActiveBudget = Boolean(budgetConfig?.budget?.isActive && budgetProgress?.period.active);
  const showRegistered = economyVisible;
  const showTarget = canEdit && hasActiveBudget && targetMode === 'PER_TASK';
  const showProgress = economyVisible && hasActiveBudget;
  const showAccountShare =
    economyVisible && hasActiveBudget && targetMode === 'AGGREGATE' && (budgetProgress?.target ?? 0) > 0;

  const breakdown = budgetProgress?.taskBreakdown ?? [];

  const loadEconomy = useCallback(async () => {
    if (!economyVisible) {
      setEconomyRows([]);
      return;
    }
    try {
      setEconomyLoading(true);
      const res = await api.getAccountTaskEconomy(accountId, { metric, granularity, anchor });
      if (res.success) {
        setEconomyRows(res.data.tasks ?? []);
      } else {
        setEconomyRows([]);
      }
    } catch {
      setEconomyRows([]);
    } finally {
      setEconomyLoading(false);
    }
  }, [accountId, metric, granularity, anchor, economyVisible]);

  useEffect(() => {
    loadEconomy();
  }, [loadEconomy]);

  const merged = useMemo(
    () => mergeTaskRows(tasks, economyRows, breakdown),
    [tasks, economyRows, breakdown]
  );

  const accountRegistered = budgetProgress?.registered ?? 0;

  const cancelTargetEdit = () => {
    setEditingTargetTaskId(null);
    setTargetDraft('');
  };

  const startTargetEdit = (taskId: string, currentTarget: number | null) => {
    setEditingTargetTaskId(taskId);
    setTargetDraft(currentTarget != null ? String(currentTarget) : '');
  };

  const saveTarget = async (taskId: string) => {
    try {
      setSavingTaskId(taskId);
      await api.upsertAccountTaskTargets(accountId, [
        { taskId, targetAmount: targetDraft === '' ? null : targetDraft }
      ]);
      cancelTargetEdit();
      onTargetsSaved?.();
    } finally {
      setSavingTaskId(null);
    }
  };

  const formatRegistered = (value: number) => formatAccountMetricValue(value, metric, currencyCode);
  const formatBudgetMetric = (value: number) => {
    const budgetMetric =
      budgetProgress?.type === 'HOURS'
        ? 'hours'
        : budgetProgress?.type === 'INTERNAL_COST'
          ? 'internal_cost'
          : 'billable';
    return formatAccountMetricValue(value, budgetMetric, currencyCode);
  };

  const tasksSectionTitle = formatTasksSectionTitle(focusWindow?.label, granularity);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{tasksSectionTitle}</Typography>
        {canEdit && (
          <Button size="small" startIcon={<AddIcon />} onClick={onAddTask}>
            Add Task
          </Button>
        )}
      </Box>

      {tasksLoading || economyLoading ? (
        <LinearProgress />
      ) : tasks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No tasks found.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>Properties</TableCell>
                {showRegistered && <TableCell align="right">Registered</TableCell>}
                {showTarget && <TableCell align="right">Target</TableCell>}
                {showProgress && <TableCell>Progress</TableCell>}
                {showAccountShare && <TableCell align="right">% of account</TableCell>}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {merged.map(({ task, economy, breakdown: rowBreakdown }) => {
                const inScope = economy?.inScope ?? rowBreakdown?.inScope ?? true;
                const registeredView = economy?.registered ?? 0;
                const registeredBudget = rowBreakdown?.registered ?? 0;
                const target = rowBreakdown?.target ?? null;
                const percent = rowBreakdown?.percent ?? null;
                const isEditingTarget = editingTargetTaskId === task.id;

                return (
                  <TableRow
                    key={task.id}
                    hover
                    sx={{ cursor: 'pointer', opacity: task.isActive ? 1 : 0.7 }}
                    onClick={() => onOpenTask(task.id, task.name)}
                  >
                    <TableCell>
                      <Typography variant="body2">{formatTaskLabel(task)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                          label={task.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={task.isActive ? 'success' : 'default'}
                        />
                        {task.isBillable && (
                          <Chip label="Billable" size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    {showRegistered && (
                      <TableCell align="right">
                        {!inScope ? '—' : formatRegistered(registeredView)}
                      </TableCell>
                    )}
                    {showTarget && (
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {!rowBreakdown?.inScope ? (
                          '—'
                        ) : isEditingTarget ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5
                            }}
                          >
                            <TextField
                              size="small"
                              value={targetDraft}
                              onChange={(e) => setTargetDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  void saveTarget(task.id);
                                }
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  cancelTargetEdit();
                                }
                              }}
                              autoFocus
                              sx={{ width: 88 }}
                              inputProps={{ style: { textAlign: 'right' } }}
                            />
                            <Button
                              size="small"
                              disabled={savingTaskId === task.id}
                              onClick={() => saveTarget(task.id)}
                            >
                              Save
                            </Button>
                            <Button size="small" onClick={cancelTargetEdit} disabled={savingTaskId === task.id}>
                              Cancel
                            </Button>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5
                            }}
                          >
                            {target != null ? (
                              <Typography variant="body2">{formatBudgetMetric(target)}</Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Not set
                              </Typography>
                            )}
                            <IconButton
                              size="small"
                              aria-label="Edit target"
                              onClick={() => startTargetEdit(task.id, target)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </TableCell>
                    )}
                    {showProgress && (
                      <TableCell>
                        {!rowBreakdown?.inScope ? (
                          '—'
                        ) : targetMode === 'PER_TASK' && target != null ? (
                          <Box sx={{ minWidth: 120 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, percent ?? 0)}
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatBudgetMetric(registeredBudget)} / {formatBudgetMetric(target)}
                            </Typography>
                          </Box>
                        ) : targetMode === 'AGGREGATE' ? (
                          <Typography variant="body2">
                            {formatBudgetMetric(registeredBudget)}
                          </Typography>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                    {showAccountShare && (
                      <TableCell align="right">
                        {!inScope || accountRegistered <= 0
                          ? '—'
                          : `${((registeredBudget / accountRegistered) * 100).toFixed(0)}%`}
                      </TableCell>
                    )}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <>
                          <IconButton size="small" onClick={() => onEditTask(task)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => onDeleteTask(task.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      <IconButton size="small" onClick={() => onOpenTask(task.id, task.name)}>
                        <AssignmentIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
